import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import { api } from '@/api'
import { cn, formatPrice, getWeekDays, formatDate, formatDateToLocal } from '@/utils'
import type { Service, Beautician } from '../../../shared/types'

export default function Booking() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  
  const [step, setStep] = useState(1)
  const [selectedService, setSelectedService] = useState<Service | null>(null)
  const [selectedBeautician, setSelectedBeautician] = useState<Beautician | null>(null)
  const [selectedDate, setSelectedDate] = useState<string>('')
  const [selectedTime, setSelectedTime] = useState<string>('')
  const [customerName, setCustomerName] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [customerWechat, setCustomerWechat] = useState('')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const { data: servicesData } = useSWR('/services', () => api.services.getAll())
  const { data: beauticiansData } = useSWR('/beauticians', () => api.beauticians.getAll())
  
  const services = servicesData?.data || []
  const beauticians = beauticiansData?.data || []
  const weekDays = getWeekDays()

  useEffect(() => {
    const serviceId = searchParams.get('service')
    if (serviceId) {
      const service = services.find(s => s.id === parseInt(serviceId))
      if (service) {
        setSelectedService(service)
        setStep(2)
      }
    }
  }, [searchParams, services])

  useEffect(() => {
    if (weekDays.length > 0 && !selectedDate) {
      setSelectedDate(formatDateToLocal(weekDays[0]))
    }
  }, [weekDays])

  const { data: availableSlotsData } = useSWR(
    selectedBeautician && selectedDate 
      ? [`slots`, selectedBeautician.id, selectedDate, selectedService?.id] 
      : null,
    () => api.beauticians.getAvailableSlots(
      selectedBeautician!.id, 
      selectedDate!, 
      selectedService?.id
    )
  )

  const availableSlots = availableSlotsData?.data || []

  const handleSubmit = async () => {
    if (!selectedService || !selectedDate || !selectedTime || !customerName || !customerPhone) {
      setError('请填写必填信息')
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      await api.appointments.create({
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_wechat: customerWechat || undefined,
        service_id: selectedService.id,
        beautician_id: selectedBeautician?.id,
        appointment_date: selectedDate,
        appointment_time: selectedTime,
        notes: notes || undefined,
      })

      navigate('/booking/success', {
        state: {
          service: selectedService,
          beautician: selectedBeautician,
          date: selectedDate,
          time: selectedTime,
        },
      })
    } catch (err: any) {
      setError(err.message || '预约失败，请稍后重试')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="page-container pb-20">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">在线预约</h1>

      <div className="mb-6">
        <div className="flex items-center justify-between">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex items-center flex-1 last:flex-none">
              <div className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
                step >= i ? 'bg-primary-500 text-white' : 'bg-primary-100 text-gray-400'
              )}>
                {i}
              </div>
              {i < 4 && (
                <div className={cn(
                  'flex-1 h-1 mx-2 rounded transition-colors',
                  step > i ? 'bg-primary-500' : 'bg-primary-100'
                )} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>选择服务</span>
          <span>选择美容师</span>
          <span>选择时间</span>
          <span>确认信息</span>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          {error}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-3">
          {services.map(service => (
            <button
              key={service.id}
              onClick={() => {
                setSelectedService(service)
                setStep(2)
              }}
              className={cn(
                'card-active w-full text-left',
                selectedService?.id === service.id && 'ring-2 ring-primary-500'
              )}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">{service.name}</h3>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">{service.description}</p>
                  <span className="text-xs text-gray-500">{service.duration}分钟</span>
                </div>
                <span className="text-lg font-bold text-primary-600">{formatPrice(service.price)}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <button
            onClick={() => {
              setSelectedBeautician(null)
              setStep(3)
            }}
            className={cn(
              'card-active w-full text-left',
              !selectedBeautician && 'ring-2 ring-primary-500'
            )}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900">不指定美容师</p>
                <p className="text-sm text-gray-500">系统自动安排</p>
              </div>
            </div>
          </button>

          {beauticians.map(beautician => (
            <button
              key={beautician.id}
              onClick={() => {
                setSelectedBeautician(beautician)
                setStep(3)
              }}
              className={cn(
                'card-active w-full text-left',
                selectedBeautician?.id === beautician.id && 'ring-2 ring-primary-500'
              )}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary-100 overflow-hidden">
                  {beautician.avatar_url ? (
                    <img src={beautician.avatar_url} alt={beautician.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-primary-500 font-medium">
                      {beautician.name[0]}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{beautician.name}</p>
                  <p className="text-sm text-gray-500">{beautician.specialty}</p>
                </div>
              </div>
            </button>
          ))}

          <button onClick={() => setStep(1)} className="btn-secondary w-full">
            返回
          </button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">选择日期</label>
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
              {weekDays.map(day => {
                const dateStr = formatDateToLocal(day)
                return (
                  <button
                    key={dateStr}
                    onClick={() => setSelectedDate(dateStr)}
                    className={cn(
                      'flex-shrink-0 w-16 py-3 rounded-xl text-center transition-colors',
                      selectedDate === dateStr
                        ? 'bg-primary-500 text-white'
                        : 'bg-white text-gray-700 border border-primary-200'
                    )}
                  >
                    <p className="text-xs">{day.toLocaleDateString('zh-CN', { weekday: 'short' })}</p>
                    <p className="text-lg font-semibold">{day.getDate()}</p>
                  </button>
                )
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">选择时间</label>
            <div className="grid grid-cols-4 gap-2">
              {availableSlots.map(time => (
                <button
                  key={time}
                  onClick={() => setSelectedTime(time)}
                  className={cn(
                    'py-2 rounded-xl text-sm font-medium transition-colors',
                    selectedTime === time
                      ? 'bg-primary-500 text-white'
                      : 'bg-white text-gray-700 border border-primary-200'
                  )}
                >
                  {time}
                </button>
              ))}
              {availableSlots.length === 0 && (
                <p className="col-span-4 text-center text-gray-500 text-sm py-4">
                  该日期暂无可用时段
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={() => setStep(2)} className="btn-secondary flex-1">
              返回
            </button>
            <button
              onClick={() => setStep(4)}
              disabled={!selectedDate || !selectedTime}
              className="btn-primary flex-1 disabled:opacity-50"
            >
              下一步
            </button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-3">预约信息</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">服务项目</span>
                <span className="text-gray-900">{selectedService?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">服务时长</span>
                <span className="text-gray-900">{selectedService?.duration}分钟</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">服务价格</span>
                <span className="text-primary-600 font-semibold">{formatPrice(selectedService?.price || 0)}</span>
              </div>
              {selectedBeautician && (
                <div className="flex justify-between">
                  <span className="text-gray-500">美容师</span>
                  <span className="text-gray-900">{selectedBeautician.name}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">预约时间</span>
                <span className="text-gray-900">{formatDate(selectedDate)} {selectedTime}</span>
              </div>
            </div>
          </div>

          <div>
            <input
              type="text"
              value={customerName}
              onChange={e => setCustomerName(e.target.value)}
              placeholder="您的姓名 *"
              className="input mb-3"
            />
            <input
              type="tel"
              value={customerPhone}
              onChange={e => setCustomerPhone(e.target.value)}
              placeholder="手机号码 *"
              className="input mb-3"
            />
            <input
              type="text"
              value={customerWechat}
              onChange={e => setCustomerWechat(e.target.value)}
              placeholder="微信号（选填）"
              className="input mb-3"
            />
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="备注信息（选填）"
              rows={3}
              className="input resize-none"
            />
          </div>

          <div className="flex gap-2">
            <button onClick={() => setStep(3)} className="btn-secondary flex-1">
              返回
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !customerName || !customerPhone}
              className="btn-primary flex-1 disabled:opacity-50"
            >
              {isSubmitting ? '提交中...' : '确认预约'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
